import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Download, ExternalLink, FileText, Loader2, QrCode, RefreshCw } from 'lucide-react';
import { publicSaasSignupService } from '../../services/api';

const POLL_MS = 10000;
const MAX_WAIT_MS = 15 * 60 * 1000;

export type PixShape = {
  payload?: string;
  encodedImage?: string;
  pixCopiaECola?: string;
  expirationDate?: string | null;
};

function pickPixPayload(p: PixShape | null | undefined): string {
  if (!p) return '';
  const s = p.payload || p.pixCopiaECola;
  return typeof s === 'string' ? s : '';
}

function qrImageSrc(encodedImage: string): string {
  const raw = String(encodedImage || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:image')) return raw;
  return `data:image/png;base64,${raw}`;
}

export type BoletoShape = {
  bankSlipUrl?: string | null;
  invoiceUrl?: string | null;
};

type PixPaymentBaseProps = {
  active: boolean;
  token: string;
  initialPix?: PixShape | null;
  paymentMethod: 'pix' | 'boleto';
  bankSlipUrl?: string | null;
  invoiceUrl?: string | null;
  onConfirmed: () => void;
  onError: (message: string) => void;
};

function usePixPaymentState(
  active: boolean,
  token: string,
  initialPix: PixShape | null | undefined,
  initialBankSlipUrl: string | null | undefined,
  initialInvoiceUrl: string | null | undefined,
  onConfirmed: () => void,
  onError: (message: string) => void
) {
  const [pix, setPix] = useState<PixShape | null | undefined>(initialPix);
  const [bankSlipUrl, setBankSlipUrl] = useState<string | null>(initialBankSlipUrl || null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(initialInvoiceUrl || null);
  const [copied, setCopied] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [lastCheckLabel, setLastCheckLabel] = useState('');
  const [panelError, setPanelError] = useState('');
  const [started] = useState(() => Date.now());

  useEffect(() => {
    if (active) {
      setPix(initialPix);
      setBankSlipUrl(initialBankSlipUrl || null);
      setInvoiceUrl(initialInvoiceUrl || null);
      setPanelError('');
      setCheckCount(0);
      setLastCheckLabel('');
      setCopied(false);
    }
  }, [active, initialPix, initialBankSlipUrl, initialInvoiceUrl, token]);

  const mergePix = useCallback((next: PixShape | null | undefined) => {
    if (!next) return;
    setPix((prev) => {
      const a = pickPixPayload(prev);
      const b = pickPixPayload(next);
      const img = next.encodedImage || prev?.encodedImage;
      if (!b && !img && !a) return prev ?? next;
      return {
        ...prev,
        ...next,
        payload: b || a || pickPixPayload(prev),
        encodedImage: img || prev?.encodedImage,
      };
    });
  }, []);

  useEffect(() => {
    if (!active || !token) return;

    let cancelled = false;
    const tick = async () => {
      try {
        if (Date.now() - started > MAX_WAIT_MS) {
          const msg =
            'Tempo máximo de espera atingido. Se já pagou, aguarde alguns minutos ou tente fazer login.';
          setPanelError(msg);
          onError(msg);
          return;
        }
        const res = await publicSaasSignupService.signupStatus(token);
        const data = res.data?.data as {
          state?: string;
          pix?: PixShape | null;
          bankSlip?: BoletoShape | null;
        };
        if (cancelled) return;

        if (data?.pix) mergePix(data.pix);
        if (data?.bankSlip?.bankSlipUrl) setBankSlipUrl(data.bankSlip.bankSlipUrl);
        if (data?.bankSlip?.invoiceUrl) setInvoiceUrl(data.bankSlip.invoiceUrl);

        setCheckCount((c) => c + 1);
        setLastCheckLabel(
          new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        );

        const state = data?.state;
        if (state === 'active') {
          onConfirmed();
          return;
        }
        if (state === 'failed') {
          const msg = 'Pagamento não concluído ou recusado. Tente novamente.';
          setPanelError(msg);
          onError(msg);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg =
            (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Erro ao consultar status do pagamento.';
          setPanelError(msg);
          onError(msg);
        }
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [active, token, started, mergePix, onConfirmed, onError]);

  const copyPayload = (payloadText: string) => {
    if (!payloadText) return;
    void navigator.clipboard.writeText(payloadText).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    });
  };

  return {
    pix,
    bankSlipUrl,
    invoiceUrl,
    copied,
    checkCount,
    lastCheckLabel,
    panelError,
    setPanelError,
    copyPayload,
  };
}

function PixPaymentPanelBody({
  paymentMethod,
  bankSlipUrl,
  invoiceUrl,
  pix,
  copied,
  checkCount,
  lastCheckLabel,
  panelError,
  setPanelError,
  copyPayload,
  titleId,
}: {
  paymentMethod: 'pix' | 'boleto';
  bankSlipUrl?: string | null;
  invoiceUrl?: string | null;
  pix: PixShape | null | undefined;
  copied: boolean;
  checkCount: number;
  lastCheckLabel: string;
  panelError: string;
  setPanelError: (v: string) => void;
  copyPayload: (payloadText: string) => void;
  titleId?: string;
}) {
  const payloadText = pickPixPayload(pix);
  const imgSrc = pix?.encodedImage ? qrImageSrc(pix.encodedImage) : '';
  const isPix = paymentMethod === 'pix';
  const boletoViewUrl = bankSlipUrl || invoiceUrl || '';
  const boletoDownloadUrl = bankSlipUrl || invoiceUrl || '';

  if (panelError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-red-700 font-medium mb-3">{panelError}</p>
        <button
          type="button"
          className="w-full py-2.5 rounded-lg bg-white border border-red-200 text-red-800 text-sm font-semibold hover:bg-red-50/80"
          onClick={() => setPanelError('')}
        >
          Tentar continuar aguardando
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <h3 id={titleId} className="text-lg font-bold text-slate-900">
          {isPix ? 'Pague com PIX' : 'Finalize o pagamento'}
        </h3>
        <p className="text-slate-600 text-sm mt-1">
          {isPix
            ? 'Escaneie o QR Code ou copie o código abaixo. Após o pagamento, você será levado à confirmação automaticamente.'
            : 'Acesse o boleto e pague. A confirmação pode levar 1 a 3 dias úteis.'}
        </p>
      </div>

      <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/90 px-4 py-3 text-left text-sm text-slate-800">
        <div className="flex items-start gap-2">
          <RefreshCw
            className="w-5 h-5 text-blue-600 shrink-0 mt-0.5 animate-spin"
            style={{ animationDuration: '3s' }}
          />
          <div>
            <p className="font-semibold text-blue-950">Aguardando confirmação</p>
            <p className="mt-1 text-xs text-slate-700">
              Verificamos a cada {POLL_MS / 1000}s se o pagamento foi reconhecido.
              {checkCount > 0 ? (
                <>
                  {' '}
                  Verificações: <strong>{checkCount}</strong>
                  {lastCheckLabel ? (
                    <>
                      {' '}
                      · Última: <strong>{lastCheckLabel}</strong>
                    </>
                  ) : null}
                </>
              ) : (
                <> Primeira verificação em instantes…</>
              )}
            </p>
          </div>
        </div>
      </div>

      {isPix && !(imgSrc || payloadText) ? (
        <div className="mb-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600 text-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="font-medium text-slate-800">Gerando QR Code e Pix copia e cola…</p>
        </div>
      ) : null}

      {isPix && (imgSrc || payloadText) ? (
        <div className="rounded-xl border border-teal-100 bg-teal-50/80 p-4 text-sm">
          <div className="flex items-center gap-2 font-bold text-teal-900 mb-3 justify-center">
            <QrCode className="w-5 h-5" /> PIX
          </div>
          {imgSrc ? (
            <div className="flex justify-center mb-4">
              <img
                src={imgSrc}
                alt="QR Code Pix"
                className="w-48 h-48 sm:w-56 sm:h-56 rounded-lg border border-teal-200 bg-white p-2"
              />
            </div>
          ) : null}
          {payloadText ? (
            <>
              <p className="text-xs font-medium text-teal-900 mb-1">Pix copia e cola</p>
              <p className="break-all font-mono text-[11px] leading-relaxed text-teal-950 max-h-28 overflow-y-auto border border-teal-100 rounded-lg p-2 bg-white">
                {payloadText}
              </p>
              <button
                type="button"
                onClick={() => copyPayload(payloadText)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copiado!' : 'Copiar código'}
              </button>
            </>
          ) : null}
          {pix?.expirationDate ? (
            <p className="text-[11px] text-teal-700 mt-3 text-center">
              Validade do QR: {String(pix.expirationDate)}
            </p>
          ) : null}
        </div>
      ) : null}

      {!isPix && !boletoViewUrl ? (
        <div className="mb-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600 text-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="font-medium text-slate-800">Gerando boleto para visualização…</p>
        </div>
      ) : null}

      {!isPix && boletoViewUrl ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50/90 p-4 text-sm">
          <div className="flex items-center gap-2 font-bold text-slate-900 mb-3 justify-center">
            <FileText className="w-5 h-5" /> Boleto bancário
          </div>
          <div className="rounded-lg overflow-hidden border border-slate-200 bg-white shadow-inner">
            <iframe
              src={boletoViewUrl}
              title="Preview do boleto"
              className="w-full h-[min(70vh,520px)] min-h-[360px] border-0 bg-white"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2 text-center">
            Se a visualização não carregar aqui, use baixar ou abrir em nova aba.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <a
              href={boletoDownloadUrl}
              download="boleto-rostech.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Baixar boleto
            </a>
            <a
              href={boletoViewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-300 bg-white text-slate-800 font-semibold hover:bg-slate-50"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir em nova aba
            </a>
          </div>
        </div>
      ) : null}

      <p className="mt-4 text-xs text-slate-500 text-center">
        {isPix
          ? 'Mantenha esta página aberta até a confirmação. O pagamento PIX costuma ser reconhecido em segundos.'
          : 'Mantenha esta página aberta até a confirmação. Boletos podem levar de 1 a 3 dias úteis para compensar.'}
      </p>
    </>
  );
}

/** QR / boleto no passo 2 do checkout, sem pop-up. */
export function PixPaymentInline({
  active,
  token,
  initialPix,
  paymentMethod,
  bankSlipUrl: initialBankSlipUrl,
  invoiceUrl: initialInvoiceUrl,
  onConfirmed,
  onError,
}: PixPaymentBaseProps) {
  const state = usePixPaymentState(
    active,
    token,
    initialPix,
    initialBankSlipUrl,
    initialInvoiceUrl,
    onConfirmed,
    onError
  );
  if (!active) return null;

  const borderClass = paymentMethod === 'boleto' ? 'border-slate-200' : 'border-teal-100';

  return (
    <div
      className={`mt-6 rounded-2xl border ${borderClass} bg-white p-5 sm:p-6 shadow-sm animate-in fade-in duration-300`}
      role="region"
      aria-labelledby="pix-inline-title"
    >
      <PixPaymentPanelBody paymentMethod={paymentMethod} titleId="pix-inline-title" {...state} />
    </div>
  );
}

/** Mantido para compatibilidade; preferir PixPaymentInline no checkout. */
export function PixPaymentModal({
  open,
  token,
  initialPix,
  paymentMethod,
  bankSlipUrl: initialBankSlipUrl,
  invoiceUrl: initialInvoiceUrl,
  onConfirmed,
  onError,
}: Omit<PixPaymentBaseProps, 'active'> & { open: boolean }) {
  const state = usePixPaymentState(
    open,
    token,
    initialPix,
    initialBankSlipUrl,
    initialInvoiceUrl,
    onConfirmed,
    onError
  );
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pix-modal-title"
    >
      <div className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <PixPaymentPanelBody paymentMethod={paymentMethod} titleId="pix-modal-title" {...state} />
      </div>
    </div>
  );
}
